/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState, useEffect } from "react";
import { UseMediaStreamResult } from "./use-media-stream-mux";

export type CameraFacingMode = 'user' | 'environment';

export interface WebcamResult extends UseMediaStreamResult {
  facingMode: CameraFacingMode;
  switchCamera: () => Promise<void>;
  canSwitchCamera: boolean;
  isSwitching: boolean;
}

export function useWebcam(): WebcamResult {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false); // Prevent multiple simultaneous switches
  
  // Device detection helpers
  const isAndroid = () => /Android/i.test(navigator.userAgent);
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMobile = () => isAndroid() || isIOS() || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Start with back camera on mobile by default, front camera on desktop
  const getDefaultFacingMode = (): CameraFacingMode => {
    return isMobile() ? 'environment' : 'user';
  };
  
  const [facingMode, setFacingMode] = useState<CameraFacingMode>(getDefaultFacingMode());
  const [canSwitchCamera, setCanSwitchCamera] = useState(false);

  useEffect(() => {
    const handleStreamEnded = () => {
      setIsStreaming(false);
      setStream(null);
    };
    if (stream) {
      stream
        .getTracks()
        .forEach((track) => track.addEventListener("ended", handleStreamEnded));
      return () => {
        stream
          .getTracks()
          .forEach((track) =>
            track.removeEventListener("ended", handleStreamEnded),
          );
      };
    }
  }, [stream]);

  // Check for multiple cameras on component mount
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        console.log(`Found ${videoDevices.length} camera(s):`, videoDevices.map(d => d.label || 'Unnamed camera'));
        setCanSwitchCamera(videoDevices.length > 1);
      } catch (error) {
        console.log('Could not enumerate devices:', error);
        setCanSwitchCamera(false);
      }
    };

    checkCameras();
  }, []);

  const start = async (requestedFacingMode?: CameraFacingMode) => {
    const currentFacingMode = requestedFacingMode || facingMode;
    console.log(`Starting camera with facing mode: ${currentFacingMode}`);
    
    try {
      // Enhanced constraints for better Android compatibility
      const constraints = {
        video: {
          facingMode: { exact: currentFacingMode }, // Use exact instead of ideal for Android
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          frameRate: { ideal: 30, max: 60 }
        },
      };

      console.log('Requesting camera with constraints:', constraints);
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Verify we got the correct camera
      const videoTrack = mediaStream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      console.log('Camera settings:', settings);
      
      setStream(mediaStream);
      setIsStreaming(true);
      setFacingMode(currentFacingMode);
      return mediaStream;
    } catch (error) {
      console.error('Error accessing camera with exact constraints:', error);
      
      // Fallback 1: Try with ideal instead of exact
      try {
        console.log('Trying fallback with ideal constraints...');
        const fallbackConstraints = {
          video: {
            facingMode: { ideal: currentFacingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
        };
        
        const fallbackStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        setStream(fallbackStream);
        setIsStreaming(true);
        setFacingMode(currentFacingMode);
        return fallbackStream;
      } catch (fallbackError) {
        console.error('Fallback with ideal constraints failed:', fallbackError);
        
        // Fallback 2: Try any available camera
        try {
          console.log('Trying any available camera...');
          const anyStream = await navigator.mediaDevices.getUserMedia({ video: true });
          setStream(anyStream);
          setIsStreaming(true);
          return anyStream;
        } catch (finalError) {
          console.error('All camera access attempts failed:', finalError);
          throw finalError;
        }
      }
    }
  };

  const stop = () => {
    if (stream) {
      // More thorough stream cleanup
      stream.getTracks().forEach((track) => {
        track.stop();
        // Remove all event listeners
        track.onended = null;
        track.onmute = null;
        track.onunmute = null;
      });
      
      // Clear the stream reference
      setStream(null);
      setIsStreaming(false);
    }
  };

  // Enhanced stop function that ensures complete cleanup for Android
  const stopStreamCompletely = (): Promise<void> => {
    return new Promise((resolve) => {
      if (stream) {
        console.log('🛑 Stopping camera stream completely...');
        
        // Get all tracks before stopping
        const tracks = stream.getTracks();
        console.log(`📊 Found ${tracks.length} tracks to stop`);
        
        // Stop all tracks with detailed logging
        tracks.forEach((track, index) => {
          console.log(`🔴 Stopping track ${index + 1}/${tracks.length}: ${track.kind}, state: ${track.readyState}, label: ${track.label}`);
          
          // Remove all event listeners first
          track.onended = null;
          track.onmute = null;
          track.onunmute = null;
          
          // Stop the track
          track.stop();
          
          // Verify track is stopped
          console.log(`✅ Track ${index + 1} stopped, new state: ${track.readyState}`);
        });
        
        // Clear stream reference immediately
        setStream(null);
        setIsStreaming(false);
        
        // Extended cleanup time for Android
        setTimeout(() => {
          console.log('🧹 Extended stream cleanup completed');
          
          // Additional verification
          tracks.forEach((track, index) => {
            console.log(`🔍 Final track ${index + 1} state: ${track.readyState}`);
          });
          
          resolve();
        }, 300); // Increased cleanup time
      } else {
        console.log('ℹ️ No stream to stop');
        resolve();
      }
    });
  };

  const switchCamera = async () => {
    if (!canSwitchCamera || isSwitching) {
      console.log('Camera switching not available or already in progress');
      return;
    }

    setIsSwitching(true);
    const currentFacingMode = facingMode;
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    
    console.log(`🔄 Switching camera from ${currentFacingMode} to ${newFacingMode}`);
    
    if (isStreaming) {
      try {
        // Android-specific camera switching approach
        console.log('📱 Detected camera switching - using Android-optimized approach');
        
        // Step 1: Stop current stream with extended cleanup
        console.log('⏹️ Stopping current stream...');
        await stopStreamCompletely();
        
        // Step 2: Platform-specific wait times for camera release
        const releaseWaitTime = isAndroid() ? 1500 : 800; // Longer wait for Android
        console.log(`⏳ Waiting ${releaseWaitTime}ms for camera resource release (${isAndroid() ? 'Android' : 'Other'})...`);
        await new Promise(resolve => setTimeout(resolve, releaseWaitTime));
        
        // Step 3: Clear any residual references
        setStream(null);
        setIsStreaming(false);
        
        // Step 4: Additional wait before requesting new camera (Android needs more time)
        const additionalWaitTime = isAndroid() ? 800 : 300;
        console.log(`⏳ Additional wait ${additionalWaitTime}ms before new camera request...`);
        await new Promise(resolve => setTimeout(resolve, additionalWaitTime));
        
        // Step 5: Update facing mode
        setFacingMode(newFacingMode);
        
        // Step 6: Request new camera with retry logic (more retries for Android)
        console.log(`📹 Starting new camera stream with ${newFacingMode} camera`);
        let retryCount = 0;
        const maxRetries = isAndroid() ? 5 : 3; // More retries for Android
        
        while (retryCount < maxRetries) {
          try {
            await start(newFacingMode);
            console.log('✅ Camera switch completed successfully');
            break;
          } catch (startError) {
            retryCount++;
            console.log(`❌ Camera start attempt ${retryCount} failed:`, startError);
            
            if (retryCount < maxRetries) {
              console.log(`🔄 Retrying in ${retryCount * 500}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryCount * 500));
            } else {
              throw startError;
            }
          }
        }
        
      } catch (error) {
        console.error('💥 Failed to switch camera after retries:', error);
        
        // Enhanced recovery: Multiple recovery attempts
        console.log('🚨 Attempting enhanced recovery...');
        let recoveryAttempt = 0;
        const maxRecoveryAttempts = 2;
        
        while (recoveryAttempt < maxRecoveryAttempts) {
          try {
            recoveryAttempt++;
            console.log(`🔧 Recovery attempt ${recoveryAttempt}...`);
            
            // Reset everything
            setStream(null);
            setIsStreaming(false);
            setFacingMode(currentFacingMode);
            
            // Wait before recovery
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Try to restart original camera
            await start(currentFacingMode);
            console.log('✅ Successfully recovered original camera');
            break;
            
          } catch (recoveryError) {
            console.error(`❌ Recovery attempt ${recoveryAttempt} failed:`, recoveryError);
            
            if (recoveryAttempt >= maxRecoveryAttempts) {
              console.error('💀 All recovery attempts failed - resetting to clean state');
              // Complete failure - reset to clean state
              setStream(null);
              setIsStreaming(false);
              setFacingMode(getDefaultFacingMode());
            }
          }
        }
      }
    } else {
      // If not streaming, just update the facing mode for next start
      setFacingMode(newFacingMode);
      console.log(`📝 Updated facing mode to ${newFacingMode} (stream not active)`);
    }
    
    setIsSwitching(false);
    console.log('🏁 Camera switching process completed');
  };

  const result: WebcamResult = {
    type: "webcam",
    start,
    stop,
    isStreaming,
    stream,
    facingMode,
    switchCamera,
    canSwitchCamera,
    isSwitching,
  };

  return result;
}
